import React from 'react';
import { ELEMENT_TYPES } from '../../types/elements';
import { CanvasElement, RendererSharedProps } from '../../types/elementRenderer';
import { useBcmHeatmap } from '../../context/BcmHeatmapContext';
import { ErdClassMeta, ErdField, computeErdHeight, ERD_LINE_H, ERD_HEADER_H_BASE, ERD_HEADER_H_STEREO, ERD_SECTION_PAD } from '../../utils/erdParser';

// C4 icon map — kind → filename in /public/images/с4/
const C4_ICON_MAP: Record<string, string> = {
  person: '01_Person_User.svg',
  'external-person': '08_External_Person.svg',
  system: '02_Software_System.svg',
  'external-system': '09_External_System.svg',
  container: '03_Container.svg',
  database: '05_Database.svg',
  component: '04_Component.svg',
  webapp: '07_Web_Browser.svg',
  mobileapp: '06_Mobile_App.svg',
  filestore: '14_File_Storage.svg',
  queue: '13_Message_Queue.svg',
  boundary: '11_System_Boundary.svg',
  'api-service': '12_API_Service.svg',
  microservice: '04_Component.svg',
  serverless: '04_Component.svg',
  controller: '04_Component.svg',
  repository: '04_Component.svg',
  service: '04_Component.svg',
  facade: '04_Component.svg',
};

const getC4IconPath = (element: CanvasElement): string | null => {
  if (!element.c4?.kind) return null;
  const file = C4_ICON_MAP[element.c4.kind];
  return file ? `/images/с4/${file}` : null;
};

const BCM_MATURITY_COLORS: Record<number, string> = {
  0: '#E2E8F0',
  1: '#FEE2E2',
  2: '#FEF3C7',
  3: '#FEF9C3',
  4: '#DCFCE7',
  5: '#DBEAFE',
};

const BCM_MATURITY_ACCENT: Record<number, string> = {
  0: '#94A3B8',
  1: '#EF4444',
  2: '#F97316',
  3: '#EAB308',
  4: '#22C55E',
  5: '#3B82F6',
};

/** Renders an icon data-URL centered over a shape, if the element has `iconDataUrl`. */
const IconOverlay: React.FC<{ el: CanvasElement }> = ({ el }) => {
  const url = el.iconDataUrl as string | null | undefined;
  if (!url) return null;
  const w = el.width ?? 0;
  const h = el.height ?? 0;
  const pad = 8;
  const iconSize = Math.max(0, Math.min(w, h) - pad * 2);
  if (iconSize <= 0) return null;
  const ix = (el.x ?? 0) + (w - iconSize) / 2;
  const iy = (el.y ?? 0) + (h - iconSize) / 2;
  return (
    <image
      href={url}
      x={ix} y={iy}
      width={iconSize} height={iconSize}
      preserveAspectRatio="xMidYMid meet"
      style={{ pointerEvents: 'none' }}
    />
  );
};

/** Renders all non-arrow, non-text element types. */
export const ShapeRenderer: React.FC<RendererSharedProps> = ({
  element, filterStyle, commonStyle, dashArray,
  onMouseDown, onDoubleClick, onContextMenu, onMouseEnter, onMouseLeave, renderLabel,
}) => {
  const heatmapMode = useBcmHeatmap();
  const ctxHandler = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); onContextMenu(e); };
  const dblHandler = (e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); onContextMenu(e); };

  switch (element.type) {
    // ─── Rectangle / Process ───────────────────────────────────────────────
    case ELEMENT_TYPES.RECTANGLE:
    case ELEMENT_TYPES.PROCESS: {
      if (element.bcm?.level === 'domain') {
        const HEADER_H = 30;
        const ex = element.x;
        const ey = element.y;
        const ew = element.width ?? 420;
        const eh = element.height ?? 320;
        const strokeColor = element.stroke ?? '#2563EB';
        const strokeW = element.strokeWidth ?? 2;
        const fillColor = element.fill ?? '#EFF6FF';
        const br = element.borderRadius ?? 4;
        const headerFill = strokeColor;
        const bodyText = element.content || 'Business Domain';

        return (
          <g
            onMouseDown={onMouseDown}
            onDoubleClick={onDoubleClick}
            onContextMenu={onContextMenu}
            style={{ cursor: 'default' }}
          >
            {/* Body */}
            <rect x={ex} y={ey} width={ew} height={eh} rx={br} ry={br}
              fill={fillColor} stroke={strokeColor} strokeWidth={strokeW} />
            {/* Header fill — clip to top rounded corners */}
            <clipPath id={`bcm-domain-hdr-${element.id}`}>
              <rect x={ex} y={ey} width={ew} height={HEADER_H + br} rx={br} ry={br} />
            </clipPath>
            <rect x={ex} y={ey} width={ew} height={HEADER_H}
              fill={headerFill}
              clipPath={`url(#bcm-domain-hdr-${element.id})`}
            />
            {/* Domain label */}
            <text
              x={ex + ew / 2} y={ey + HEADER_H / 2}
              textAnchor="middle" dominantBaseline="middle"
              fill="#FFFFFF" fontSize={13} fontWeight={700}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {bodyText}
            </text>
            {/* Selection outline */}
            {element.selected && (
              <rect x={ex - 2} y={ey - 2} width={ew + 4} height={eh + 4}
                rx={br + 2} fill="none"
                stroke="#60A5FA" strokeWidth={2} strokeDasharray="6 3"
              />
            )}
          </g>
        );
      }

      if (element.bcm?.level === 'capability') {
        const ex = element.x;
        const ey = element.y;
        const ew = element.width ?? 180;
        const eh = element.height ?? 90;
        const strokeColor = element.stroke ?? '#94A3B8';
        const strokeW = element.strokeWidth ?? 1;
        const br = element.borderRadius ?? 4;
        const maturity = element.bcm.maturity ?? 0;
        const heatFill = heatmapMode === 'maturity' && maturity > 0
          ? (BCM_MATURITY_ACCENT[maturity] ?? BCM_MATURITY_ACCENT[0]) + 'AA'
          : (BCM_MATURITY_COLORS[maturity] ?? BCM_MATURITY_COLORS[0]);
        const accentColor = BCM_MATURITY_ACCENT[maturity] ?? BCM_MATURITY_ACCENT[0];
        const DOT_R = 7;
        const bodyText = element.content || 'Capability';
        const textColor = element.textColor ?? '#1E293B';

        return (
          <g
            onMouseDown={onMouseDown}
            onDoubleClick={onDoubleClick}
            onContextMenu={onContextMenu}
            style={{ cursor: 'default' }}
          >
            {/* Background */}
            <rect x={ex} y={ey} width={ew} height={eh} rx={br} ry={br}
              fill={heatFill} stroke={strokeColor} strokeWidth={strokeW} />
            {/* Left accent strip */}
            <clipPath id={`bcm-cap-strip-${element.id}`}>
              <rect x={ex} y={ey} width={6} height={eh} rx={br} ry={br} />
            </clipPath>
            <rect x={ex} y={ey} width={6} height={eh}
              fill={accentColor}
              clipPath={`url(#bcm-cap-strip-${element.id})`}
            />
            {/* Capability name */}
            <text
              x={ex + 14} y={ey + eh / 2}
              textAnchor="start" dominantBaseline="middle"
              fill={textColor} fontSize={12} fontWeight={500}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {bodyText}
            </text>
            {/* Maturity dot (bottom-right) */}
            {maturity > 0 && (
              <g>
                <circle cx={ex + ew - DOT_R - 6} cy={ey + eh - DOT_R - 4} r={DOT_R}
                  fill={accentColor} />
                <text
                  x={ex + ew - DOT_R - 6} y={ey + eh - DOT_R - 4}
                  textAnchor="middle" dominantBaseline="middle"
                  fill="#FFFFFF" fontSize={8} fontWeight={700}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  {String(maturity)}
                </text>
              </g>
            )}
            {/* Owner label (top-right) */}
            {element.bcm.owner && (
              <text
                x={ex + ew - 6} y={ey + 10}
                textAnchor="end" dominantBaseline="middle"
                fill="#64748B" fontSize={9}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {element.bcm.owner}
              </text>
            )}
            {/* Selection */}
            {element.selected && (
              <rect x={ex - 2} y={ey - 2} width={ew + 4} height={eh + 4}
                rx={br + 2} fill="none"
                stroke="#60A5FA" strokeWidth={2} strokeDasharray="6 3"
              />
            )}
          </g>
        );
      }

      if (element.bcm?.level === 'subcapability') {
        const ex = element.x;
        const ey = element.y;
        const ew = element.width ?? 150;
        const eh = element.height ?? 60;
        const strokeColor = element.stroke ?? '#CBD5E1';
        const strokeW = element.strokeWidth ?? 1;
        const fillColor = element.fill ?? '#F8FAFC';
        const br = element.borderRadius ?? 3;
        const maturity = element.bcm.maturity ?? 0;
        const accentColor = BCM_MATURITY_ACCENT[maturity] ?? BCM_MATURITY_ACCENT[0];
        const bodyText = element.content || 'Sub-capability';
        const textColor = element.textColor ?? '#475569';

        return (
          <g
            onMouseDown={onMouseDown}
            onDoubleClick={onDoubleClick}
            onContextMenu={onContextMenu}
            style={{ cursor: 'default' }}
          >
            <rect x={ex} y={ey} width={ew} height={eh} rx={br} ry={br}
              fill={fillColor} stroke={strokeColor} strokeWidth={strokeW} />
            {/* Bottom accent line */}
            <line
              x1={ex + br} y1={ey + eh}
              x2={ex + ew - br} y2={ey + eh}
              stroke={accentColor} strokeWidth={2}
            />
            <text
              x={ex + ew / 2} y={ey + eh / 2}
              textAnchor="middle" dominantBaseline="middle"
              fill={textColor} fontSize={11}
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              {bodyText}
            </text>
            {element.selected && (
              <rect x={ex - 2} y={ey - 2} width={ew + 4} height={eh + 4}
                rx={br + 2} fill="none"
                stroke="#60A5FA" strokeWidth={2} strokeDasharray="6 3"
              />
            )}
          </g>
        );
      }

      if (element.isDatabase) {
        const w = element.width!;
        const h = element.height!;
        const rx = w / 2;
        const ry = Math.min(h * 0.15, w * 0.2);
        const cx = element.x + w / 2;
        const topY = element.y + ry;
        const bottomY = element.y + h - ry;
        const midY1 = element.y + h * 0.35;
        const midY2 = element.y + h * 0.55;
        return (
          <g onContextMenu={ctxHandler} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <ellipse cx={cx} cy={element.y + ry} rx={rx} ry={ry} fill={element.fill} stroke={element.stroke} strokeWidth={element.strokeWidth} strokeDasharray={dashArray} style={commonStyle} onMouseDown={onMouseDown} onDoubleClick={dblHandler} />
            <rect x={element.x} y={topY} width={w} height={Math.max(0, h - 2 * ry)} fill={element.fill} stroke="none" style={commonStyle} onMouseDown={onMouseDown} onDoubleClick={dblHandler} />
            <line x1={element.x} y1={topY} x2={element.x} y2={bottomY} stroke={element.stroke} strokeWidth={element.strokeWidth} strokeDasharray={dashArray} />
            <line x1={element.x + w} y1={topY} x2={element.x + w} y2={bottomY} stroke={element.stroke} strokeWidth={element.strokeWidth} strokeDasharray={dashArray} />
            {h > 80 && (
              <>
                <ellipse cx={cx} cy={midY1} rx={rx} ry={ry * 0.8} fill="none" stroke={element.stroke} strokeWidth={(element.strokeWidth || 1) * 0.7} opacity={0.4} />
                <ellipse cx={cx} cy={midY2} rx={rx} ry={ry * 0.8} fill="none" stroke={element.stroke} strokeWidth={(element.strokeWidth || 1) * 0.7} opacity={0.4} />
              </>
            )}
            <path d={`M ${element.x} ${bottomY} A ${rx} ${ry} 0 0 0 ${element.x + w} ${bottomY}`} fill="none" stroke={element.stroke} strokeWidth={element.strokeWidth} strokeDasharray={dashArray} />
            <path d={`M ${element.x} ${element.y + ry} A ${rx} ${ry} 0 0 1 ${element.x + w} ${element.y + ry}`} fill="none" stroke={element.stroke} strokeWidth={(element.strokeWidth || 1) * 0.6} strokeDasharray="3 3" opacity={0.3} />
            {/* oxlint-disable-next-line react/no-unstable-nested-components */}
            {renderLabel()}
          </g>
        );
      }

      const isC4Rect = !!(element.c4?.kind);

      if (isC4Rect && element.width && element.height) {
        const kind = element.c4!.kind as string;
        const isPerson = kind === 'person' || kind === 'external-person';
        const fill   = element.fill   ?? '#1168BD';
        const stroke = element.stroke ?? '#0E5AA7';
        const sw = element.strokeWidth ?? 2;
        const br = element.borderRadius ?? 8;
        const ex = element.x, ey = element.y, ew = element.width, eh = element.height;

        if (isPerson) {
          const headR  = Math.round(ew * 0.22);
          const headCx = ex + ew / 2;
          const headCy = ey + headR + 2;
          const bodyY  = headCy + headR + 10;
          const bodyH  = Math.max(0, ey + eh - bodyY);
          return (
            <g onContextMenu={ctxHandler} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
              <circle cx={headCx} cy={headCy} r={headR} fill={fill} stroke={stroke} strokeWidth={sw} style={commonStyle} onMouseDown={onMouseDown} onDoubleClick={dblHandler} />
              <rect x={ex} y={bodyY} width={ew} height={bodyH} rx={br} fill={fill} stroke={stroke} strokeWidth={sw} style={commonStyle} onMouseDown={onMouseDown} onDoubleClick={dblHandler} />
              {/* oxlint-disable-next-line react/no-unstable-nested-components */}
              {renderLabel()}
            </g>
          );
        }

        return (
          <g onContextMenu={ctxHandler} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <rect x={ex} y={ey} width={ew} height={eh} rx={br} fill={fill} stroke={stroke} strokeWidth={sw} style={commonStyle} onMouseDown={onMouseDown} onDoubleClick={dblHandler} />
            {/* oxlint-disable-next-line react/no-unstable-nested-components */}
            {renderLabel()}
          </g>
        );
      }

      return (
        <g onContextMenu={ctxHandler} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
          <rect
            x={element.x} y={element.y} width={element.width} height={element.height}
            fill={element.fill}
            stroke={element.stroke}
            strokeWidth={element.strokeWidth}
            strokeDasharray={dashArray}
            rx={element.borderRadius} style={commonStyle}
            onMouseDown={onMouseDown} onDoubleClick={dblHandler}
          />
          <IconOverlay el={element} />
          {/* oxlint-disable-next-line react/no-unstable-nested-components */}
          {renderLabel()}
        </g>
      );
    }

    // ─── Circle / Interface ────────────────────────────────────────────────
    case ELEMENT_TYPES.CIRCLE:
    case ELEMENT_TYPES.INTERFACE:
      return (
        <g onContextMenu={ctxHandler} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
          <circle cx={element.x + element.radius!} cy={element.y + element.radius!} r={element.radius} fill={element.fill} stroke={element.stroke} strokeWidth={element.strokeWidth} strokeDasharray={dashArray} style={commonStyle} onMouseDown={onMouseDown} onDoubleClick={dblHandler} />
          {/* oxlint-disable-next-line react/no-unstable-nested-components */}
          {renderLabel()}
        </g>
      );

    // ─── Ellipse / UseCase / Terminator ────────────────────────────────────
    case ELEMENT_TYPES.ELLIPSE:
    case ELEMENT_TYPES.USE_CASE:
    case ELEMENT_TYPES.TERMINATOR: {
      const rx = element.width! / 2;
      const ry = element.height! / 2;
      if (element.type === ELEMENT_TYPES.TERMINATOR) {
        const r = element.borderRadius || element.height! / 2;
        return (
          <g onContextMenu={ctxHandler} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <rect x={element.x} y={element.y} width={element.width} height={element.height} rx={r} fill={element.fill} stroke={element.stroke} strokeWidth={element.strokeWidth} strokeDasharray={dashArray} style={commonStyle} onMouseDown={onMouseDown} onDoubleClick={dblHandler} />
            {/* oxlint-disable-next-line react/no-unstable-nested-components */}
            {renderLabel()}
          </g>
        );
      }
      return (
        <g onContextMenu={ctxHandler} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
          <ellipse cx={element.x + rx} cy={element.y + ry} rx={rx} ry={ry} fill={element.fill} stroke={element.stroke} strokeWidth={element.strokeWidth} strokeDasharray={dashArray} style={commonStyle} onMouseDown={onMouseDown} onDoubleClick={dblHandler} />
          {/* oxlint-disable-next-line react/no-unstable-nested-components */}
          {renderLabel()}
        </g>
      );
    }

    // ─── All path-based shapes ─────────────────────────────────────────────
    case ELEMENT_TYPES.TRIANGLE:
    case ELEMENT_TYPES.DIAMOND:
    case ELEMENT_TYPES.DECISION:
    case ELEMENT_TYPES.PARALLELOGRAM:
    case ELEMENT_TYPES.DATA:
    case ELEMENT_TYPES.DOCUMENT:
    case ELEMENT_TYPES.CYLINDER:
    case ELEMENT_TYPES.DATABASE:
    case ELEMENT_TYPES.CLOUD:
    case ELEMENT_TYPES.PREPARATION:
    case ELEMENT_TYPES.MANUAL_INPUT:
    case ELEMENT_TYPES.MANUAL_OPERATION:
    case ELEMENT_TYPES.DISPLAY:
    case ELEMENT_TYPES.INTERNAL_STORAGE:
    case ELEMENT_TYPES.QUEUE:
    case ELEMENT_TYPES.L_RECT:
    case ELEMENT_TYPES.U_RECT:
    case ELEMENT_TYPES.TRAPEZOID:
    case ELEMENT_TYPES.INV_TRAPEZOID:
    case ELEMENT_TYPES.STEP:
    case ELEMENT_TYPES.CALLOUT:
    case ELEMENT_TYPES.ACTOR:
    case ELEMENT_TYPES.LOOP_LIMIT:
    case ELEMENT_TYPES.STORAGE:
    case ELEMENT_TYPES.PACKAGE:
    case ELEMENT_TYPES.COMPONENT: {
      const { x, y, width: w = 0, height: h = 0 } = element;
      const mx = x + w / 2;
      const my = y + h / 2;
      const r = x + w;
      const b = y + h;
      let d = '';

      switch (element.type) {
        case ELEMENT_TYPES.TRIANGLE:
          d = `M ${mx} ${y} L ${r} ${b} L ${x} ${b} Z`;
          break;
        case ELEMENT_TYPES.DIAMOND:
        case ELEMENT_TYPES.DECISION:
          d = `M ${mx} ${y} L ${r} ${my} L ${mx} ${b} L ${x} ${my} Z`;
          break;
        case ELEMENT_TYPES.PARALLELOGRAM:
        case ELEMENT_TYPES.DATA: {
          const off = w * 0.2;
          d = `M ${x + off} ${y} L ${r} ${y} L ${r - off} ${b} L ${x} ${b} Z`;
          break;
        }
        case ELEMENT_TYPES.DOCUMENT: {
          const waveH = h * 0.15;
          d = `M ${x} ${y} L ${r} ${y} L ${r} ${b - waveH} Q ${x + w * 0.75} ${b} ${mx} ${b - waveH} Q ${x + w * 0.25} ${b - 2 * waveH} ${x} ${b - waveH} Z`;
          break;
        }
        case ELEMENT_TYPES.CYLINDER:
        case ELEMENT_TYPES.DATABASE: {
          const ry2 = h * 0.15;
          const rx2 = w / 2;
          const topCy = y + ry2;
          const botCy = b - ry2;
          d = `M ${x} ${topCy} A ${rx2} ${ry2} 0 1 1 ${r} ${topCy} A ${rx2} ${ry2} 0 1 1 ${x} ${topCy} ` +
            `M ${x} ${topCy} L ${x} ${botCy} A ${rx2} ${ry2} 0 0 0 ${r} ${botCy} L ${r} ${topCy}`;
          break;
        }
        case ELEMENT_TYPES.CLOUD:
          d = `M ${x + w * 0.2} ${my} C ${x} ${y + h * 0.2} ${mx} ${y - h * 0.2} ${mx} ${y + h * 0.1} C ${mx} ${y - h * 0.2} ${r} ${y + h * 0.2} ${r - w * 0.2} ${my} C ${r} ${b - h * 0.2} ${mx} ${b + h * 0.2} ${mx} ${b - h * 0.1} C ${mx} ${b + h * 0.2} ${x} ${b - h * 0.2} ${x + w * 0.2} ${my} Z`;
          break;
        case ELEMENT_TYPES.PREPARATION: {
          const off = w * 0.2;
          d = `M ${x} ${my} L ${x + off} ${y} L ${r - off} ${y} L ${r} ${my} L ${r - off} ${b} L ${x + off} ${b} Z`;
          break;
        }
        case ELEMENT_TYPES.MANUAL_INPUT: {
          const offY = h * 0.2;
          d = `M ${x} ${y + offY} L ${r} ${y} L ${r} ${b} L ${x} ${b} Z`;
          break;
        }
        case ELEMENT_TYPES.MANUAL_OPERATION: {
          const off = w * 0.15;
          d = `M ${x} ${y} L ${r} ${y} L ${r - off} ${b} L ${x + off} ${b} Z`;
          break;
        }
        case ELEMENT_TYPES.DISPLAY: {
          const off = w * 0.2;
          d = `M ${x} ${my} L ${x + off} ${y} L ${r - off} ${y} Q ${r} ${y} ${r} ${my} Q ${r} ${b} ${r - off} ${b} L ${x + off} ${b} Z`;
          break;
        }
        case ELEMENT_TYPES.INTERNAL_STORAGE: {
          const off = w * 0.15;
          d = `M ${x} ${y} L ${r} ${y} L ${r} ${b} L ${x} ${b} Z M ${x + off} ${y} L ${x + off} ${b} M ${x} ${y + off} L ${r} ${y + off}`;
          break;
        }
        case ELEMENT_TYPES.QUEUE: {
          const rQ = h / 2;
          d = `M ${x + rQ} ${y} L ${r} ${y} Q ${r - rQ / 2} ${my} ${r} ${b} L ${x + rQ} ${b} A ${rQ} ${rQ} 0 1 1 ${x + rQ} ${y} Z`;
          break;
        }
        case ELEMENT_TYPES.L_RECT:
          d = `M ${x} ${y} L ${x + w * 0.3} ${y} L ${x + w * 0.3} ${y + h * 0.7} L ${r} ${y + h * 0.7} L ${r} ${b} L ${x} ${b} Z`;
          break;
        case ELEMENT_TYPES.U_RECT:
          d = `M ${x} ${y} L ${x + w * 0.25} ${y} L ${x + w * 0.25} ${b - h * 0.25} L ${r - w * 0.25} ${b - h * 0.25} L ${r - w * 0.25} ${y} L ${r} ${y} L ${r} ${b} L ${x} ${b} Z`;
          break;
        case ELEMENT_TYPES.TRAPEZOID: {
          const off = w * 0.2;
          d = `M ${x + off} ${y} L ${r - off} ${y} L ${r} ${b} L ${x} ${b} Z`;
          break;
        }
        case ELEMENT_TYPES.INV_TRAPEZOID: {
          const off = w * 0.2;
          d = `M ${x} ${y} L ${r} ${y} L ${r - off} ${b} L ${x + off} ${b} Z`;
          break;
        }
        case ELEMENT_TYPES.STEP: {
          const off = w * 0.2;
          d = `M ${x} ${y} L ${r - off} ${y} L ${r} ${my} L ${r - off} ${b} L ${x} ${b} Z`;
          break;
        }
        case ELEMENT_TYPES.CALLOUT:
          d = `M ${x} ${y} L ${r} ${y} L ${r} ${b - 20} L ${x + 40} ${b - 20} L ${x + 20} ${b} L ${x + 20} ${b - 20} L ${x} ${b - 20} Z`;
          break;
        case ELEMENT_TYPES.ACTOR: {
          const headR = w / 4;
          const neckY = y + headR * 2;
          const legY = b - h * 0.3;
          d = `M ${mx} ${y + headR} m -${headR}, 0 a ${headR},${headR} 0 1,0 ${headR * 2},0 a ${headR},${headR} 0 1,0 -${headR * 2},0 `;
          d += `M ${mx} ${neckY} L ${mx} ${legY} `;
          d += `M ${x} ${neckY + 10} L ${r} ${neckY + 10} `;
          d += `M ${mx} ${legY} L ${x} ${b} M ${mx} ${legY} L ${r} ${b}`;
          break;
        }
        case ELEMENT_TYPES.LOOP_LIMIT: {
          const off = w * 0.15;
          d = `M ${x} ${y} L ${r - off} ${y} L ${r} ${y + off} L ${r} ${b} L ${x} ${b} Z`;
          break;
        }
        case ELEMENT_TYPES.STORAGE: {
          const stR = w * 0.2;
          d = `M ${x} ${y} A ${stR} ${h} 0 0 0 ${x} ${b} L ${r} ${b} A ${stR} ${h} 0 0 0 ${r} ${y} L ${x} ${y}`;
          break;
        }
        case ELEMENT_TYPES.PACKAGE: {
          const tabH = h * 0.2;
          const tabW = w * 0.4;
          d = `M ${x} ${y + tabH} L ${x + tabW} ${y + tabH} L ${x + tabW} ${y} L ${x} ${y} Z M ${x} ${y + tabH} L ${r} ${y + tabH} L ${r} ${b} L ${x} ${b} L ${x} ${y + tabH}`;
          break;
        }
        case ELEMENT_TYPES.COMPONENT: {
          const boxW = w * 0.2;
          const boxH = h * 0.2;
          d = `M ${x + boxW / 2} ${y} L ${r} ${y} L ${r} ${b} L ${x + boxW / 2} ${b} Z`;
          d += ` M ${x} ${y + boxH} L ${x + boxW} ${y + boxH} L ${x + boxW} ${y + 2 * boxH} L ${x} ${y + 2 * boxH} Z`;
          d += ` M ${x} ${b - 2 * boxH} L ${x + boxW} ${b - 2 * boxH} L ${x + boxW} ${b - boxH} L ${x} ${b - boxH} Z`;
          break;
        }
        default:
          break;
      }

      return (
        <g onContextMenu={ctxHandler} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
          <path d={d} fill={element.fill} stroke={element.stroke} strokeWidth={element.strokeWidth} strokeDasharray={dashArray} style={commonStyle} onMouseDown={onMouseDown} onDoubleClick={dblHandler} />
          {/* oxlint-disable-next-line react/no-unstable-nested-components */}
          {renderLabel()}
        </g>
      );
    }

    // ─── ERD Class ────────────────────────────────────────────────────────────
    case 'erd-class': {
      const erd = (element as any).erd as ErdClassMeta | undefined;
      if (!erd) return null;

      const ex = element.x;
      const ey = element.y;
      const ew = element.width ?? 180;
      const headerH = erd.stereotype ? ERD_HEADER_H_STEREO : ERD_HEADER_H_BASE;
      const fieldsH = (erd.fields.length > 0 ? erd.fields.length * ERD_LINE_H : 0) + ERD_SECTION_PAD * 2;
      const methodsH = erd.methods.length > 0
        ? erd.methods.length * ERD_LINE_H + ERD_SECTION_PAD * 2
        : 0;
      const totalH = headerH + fieldsH + methodsH;

      const HEADER_COLORS: Record<string, string> = {
        class: '#3B82F6',
        interface: '#22C55E',
        enum: '#F97316',
        abstract: '#6B7280',
      };
      const headerFill = HEADER_COLORS[erd.kind] ?? '#3B82F6';
      const bodyFill = element.fill ?? '#FFFFFF';
      const textFill = '#1E293B';
      const borderColor = element.stroke ?? '#94A3B8';
      const strokeW = element.strokeWidth ?? 1;

      const renderMember = (f: ErdField, yOff: number) => {
        const vis = f.visibility === '-' ? '−' : f.visibility;
        const label = `${vis} ${f.name}${f.type ? ': ' + f.type : ''}`;
        return (
          <text
            key={`${f.name}-${yOff}`}
            x={ex + 8}
            y={ey + yOff + ERD_LINE_H / 2}
            dominantBaseline="middle"
            fill={textFill}
            fontSize={11}
            fontStyle={f.abstract ? 'italic' : 'normal'}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {label}
          </text>
        );
      };

      return (
        <g
          onMouseDown={onMouseDown}
          onDoubleClick={dblHandler}
          onContextMenu={ctxHandler}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          style={{ cursor: 'default' }}
        >
          {/* Body */}
          <rect x={ex} y={ey} width={ew} height={totalH}
            fill={bodyFill} stroke={borderColor} strokeWidth={strokeW} rx={2} />
          {/* Header background */}
          <clipPath id={`erd-hdr-${element.id}`}>
            <rect x={ex} y={ey} width={ew} height={headerH} rx={2} />
          </clipPath>
          <rect x={ex} y={ey} width={ew} height={headerH}
            fill={headerFill} clipPath={`url(#erd-hdr-${element.id})`} />
          {/* Stereotype label */}
          {erd.stereotype && (
            <text
              x={ex + ew / 2}
              y={ey + 10}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#FFFFFF"
              fontSize={9}
              fontStyle="italic"
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              «{erd.stereotype}»
            </text>
          )}
          {/* Class name */}
          <text
            x={ex + ew / 2}
            y={erd.stereotype ? ey + headerH - 10 : ey + headerH / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#FFFFFF"
            fontSize={12}
            fontWeight={700}
            fontStyle={erd.kind === 'abstract' ? 'italic' : 'normal'}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {erd.name}
          </text>
          {/* Fields divider */}
          <line x1={ex} y1={ey + headerH} x2={ex + ew} y2={ey + headerH}
            stroke={borderColor} strokeWidth={1} />
          {/* Fields */}
          {erd.fields.map((f, i) =>
            renderMember(f, headerH + ERD_SECTION_PAD + i * ERD_LINE_H)
          )}
          {/* Methods divider */}
          {erd.methods.length > 0 && (
            <line x1={ex} y1={ey + headerH + fieldsH} x2={ex + ew} y2={ey + headerH + fieldsH}
              stroke={borderColor} strokeWidth={1} />
          )}
          {/* Methods */}
          {erd.methods.map((m, i) =>
            renderMember(m, headerH + fieldsH + ERD_SECTION_PAD + i * ERD_LINE_H)
          )}
          {/* Selection outline */}
          {element.selected && (
            <rect x={ex - 2} y={ey - 2} width={ew + 4} height={totalH + 4}
              fill="none" stroke="#60A5FA" strokeWidth={2} strokeDasharray="6 3" rx={2} />
          )}
        </g>
      );
    }

    // ─── Fallback: unknown types with a bbox ───────────────────────────────
    default:
      if (typeof element.x === 'number' && typeof element.y === 'number' && typeof element.width === 'number' && typeof element.height === 'number') {
        return (
          <g onContextMenu={ctxHandler} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <rect x={element.x} y={element.y} width={element.width} height={element.height} fill={element.fill} stroke={element.stroke} strokeWidth={element.strokeWidth} strokeDasharray={dashArray} rx={element.borderRadius} style={commonStyle} onMouseDown={onMouseDown} onDoubleClick={dblHandler} />
            {/* oxlint-disable-next-line react/no-unstable-nested-components */}
            {renderLabel()}
          </g>
        );
      }
      return null;
  }
};
